<template>
  <!-- Loading state -->
  <div v-if="loading" class="text-center py-12">
    <p class="mt-4 text-gray-600">Loading RFIs data...</p>
    <MainLoader />
  </div>

  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" v-else>
    <RFIHeader />
    <div class="flex justify-center">
      <button
        @click="openCreateModal"
        class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-accent-gradient hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 transition-colors duration-200 mr-4"
      >
        Add RFI ➕
      </button>

      <button
        @click="handleExportToExcel"
        :disabled="exporting"
        class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand-700 hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg
          v-if="!exporting"
          class="h-4 w-4 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <div
          v-else
          class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"
        ></div>
        {{ exporting ? "Exporting..." : "Export Log" }}
      </button>
    </div>
    <RFIErrorState v-if="error" :error="error" />

    <div
      v-if="!loading && !error"
      class="bg-white shadow overflow-hidden sm:rounded-lg"
    >
      <RFIFilters
        v-model:searchQuery="searchQuery"
        v-model:priorityFilter="priorityFilter"
        v-model:disciplineFilter="disciplineFilter"
        v-model:activeView="activeView"
        :rfisRequested="rfisRequested"
        :rfisDirected="rfisDirected"
        :statuses="statuses"
        :disciplines="disciplines"
      />

      <RFICards
        v-if="filteredRfis.length > 0"
        :rfis="paginatedRfis"
        :expandedRfis="expandedRfis"
        @toggle-details="toggleRfiDetails"
        class="sm:hidden"
      />

      <RFITable
        v-if="filteredRfis.length > 0"
        :rfis="paginatedRfis"
        @view-details="viewRfiDetails"
        class="hidden sm:block"
        :userCols="userColumns"
      />

      <RFIEmptyState v-if="!loading && activeRfis.length === 0" />

      <RFIPagination
        v-if="filteredRfis.length > 0"
        v-model:currentPage="currentPage"
        :totalPages="totalPages"
        :filteredRfis="filteredRfis"
        :itemsPerPage="itemsPerPage"
      />
    </div>

    <RFIModal
      v-if="selectedRfi || showCreateModal"
      :rfi="selectedRfi"
      :editMode="editMode"
      :editedRfi="editMode ? editedRfi : newRfi"
      :saving="saving"
      :statuses="statuses"
      :disciplines="disciplines"
      :isCreateMode="showCreateModal"
      :isLoading="loadingModal"
      :priorities="priorities"
      @close="closeModal"
      @toggle-edit="toggleEditMode"
      @save="saveChanges"
      @create="createNewRfi"
      @update-field="handleUpdateField"
      :editableFields="editableFields"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import MainLoader from "@/components/MainLoader.vue";
import RFIHeader from "./RFIHeader.vue";
import RFIFilters from "./RFIFilters.vue";
import RFITable from "./RFITable.vue";
import RFICards from "./RFICards.vue";
import RFIPagination from "./RFIPagination.vue";
import RFIModal from "./RFIModal.vue";
import RFIEmptyState from "./RFIEmptyState.vue";
import RFIErrorState from "./RFIErrorState.vue";
import { useExcelExport } from "@/helpers/useExcelExport";

export default {
  components: {
    MainLoader,
    RFIHeader,
    RFIFilters,
    RFITable,
    RFICards,
    RFIPagination,
    RFIModal,
    RFIEmptyState,
    RFIErrorState,
  },

  setup() {
    const store = useStore();
    const currentUser = store.state.users.user;
    console.log("🚀 ~ setup ~ currentUser:", currentUser);
    const { exportToExcel, transformRFIArray } = useExcelExport();
    const allRfis = ref([]);
    const rfisRequested = ref([]);
    const rfisDirected = ref([]);
    const loading = ref(true);
    const loadingModal = ref(false);
    const error = ref(null);
    const searchQuery = ref("");
    const priorityFilter = ref("");
    const disciplineFilter = ref("");
    const expandedRfis = ref([]);
    const selectedRfi = ref(null);
    const currentPage = ref(1);
    const itemsPerPage = 10;
    const activeView = ref("requested");
    const editMode = ref(false);
    const editedRfi = ref({});
    const saving = ref(false);
    const editableFields = ref([]);
    const userColumns = ref([]);
    const showCreateModal = ref(false);

    const newRfi = ref({
      requestor_name: `${currentUser.first_name} ${currentUser.last_name}`,
      requestor_email: currentUser.email,
      company: currentUser.company,
    });

    const priorities = ref(["Routine", "Expedite"]);
    const exporting = ref(false);

    const openCreateModal = () => {
      showCreateModal.value = true;
      selectedRfi.value = null;
    };

    const createNewRfi = async (attachments) => {
      loadingModal.value = true;
      saving.value = true;
      try {
        const payload = {};

        editableFields.value.forEach((field) => {
          if (field.editable && newRfi.value[field.key] !== undefined) {
            payload[field.key] = newRfi.value[field.key];
          }
        });

        await store.dispatch("createRFI", {
          rfiData: payload,
          attachments: attachments,
        });

        showCreateModal.value = false;
        newRfi.value = {};
        await fetchRfis();
      } catch (err) {
        console.error("Error creating RFI:", err);
        error.value = err.message || "Failed to create RFI";
      } finally {
        saving.value = false;
        loadingModal.value = false;
      }
    };

    const toggleEditMode = () => {
      editMode.value = !editMode.value;
      if (editMode.value) {
        editedRfi.value = JSON.parse(JSON.stringify(selectedRfi.value));
      }
    };

    const closeModal = () => {
      editMode.value = false;
      showCreateModal.value = false;
      selectedRfi.value = null;
    };

    const saveChanges = async () => {
      console.log("saving changes?....");
      loadingModal.value = true;
      saving.value = true;
      try {
        const payload = {
          ROW_ID: selectedRfi.value.ROW_ID,
        };

        editableFields.value.forEach((field) => {
          if (field.editable) {
            const originalValue = selectedRfi.value[field.key];
            const editedValue = editedRfi.value[field.key];

            if (JSON.stringify(originalValue) !== JSON.stringify(editedValue)) {
              payload[field.key] = editedValue;
            }
          }
        });

        if (Object.keys(payload).length === 1) {
          console.log("There is no changes");
          editMode.value = false;
          return;
        }
        if (payload.directed_to_email) {
          payload.assigned_by = currentUser.email;
        }

        await store.dispatch("updateRFI", payload);

        const index = activeRfis.value.findIndex(
          (rfi) => rfi.ID === selectedRfi.value.ID
        );
        if (index !== -1) {
          activeRfis.value[index] = { ...editedRfi.value };
        }

        editMode.value = false;
        closeModal();
      } catch (err) {
        console.error("Error saving RFI:", err);
      } finally {
        loadingModal.value = false;
        saving.value = false;
      }
    };

    const fetchRfis = async () => {
      try {
        loading.value = true;
        const userCols = await store.dispatch("getUserCols", {
          id_user: currentUser.id_user,
        });
        if (!userCols) {
          error.value = "Columns for user not configurated.";
        }

        const ssConfig = await store.dispatch("getSSConfig");
        userColumns.value = ssConfig.filter((item) =>
          userCols?.includes(item.id)
        );
        editableFields.value = ssConfig
          .filter(
            (item) => item.match_key !== "SHEET" && item.match_key !== "ID"
          )
          .map((item) => ({
            id: item.id,
            key: item.match_key,
            label: item.title,
            editable: true,
          }));
        const response = await store.dispatch("fetchRFIs");

        if (!response.ok) {
          throw new Error("Failed to fetch RFIs");
        }

        const data = await response.data;

        if (data.allData) {
          allRfis.value = data.allData;
        } else {
          rfisRequested.value = data.requestorMatches || [];
          rfisDirected.value = data.directedToMatches || [];
        }
      } catch (err) {
        error.value = err.message;
        console.error("Error fetching RFIs:", err);
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      const options = { year: "numeric", month: "short", day: "numeric" };
      return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const priorityClasses = (priority) => {
      const priorityMap = {
        High: "bg-red-100 text-red-800",
        Medium: "bg-yellow-100 text-yellow-800",
        Low: "bg-green-100 text-green-800",
      };
      return priorityMap[priority] || "bg-gray-100 text-gray-800";
    };

    const toggleRfiDetails = (rfiId) => {
      const index = expandedRfis.value.indexOf(rfiId);
      if (index === -1) {
        expandedRfis.value.push(rfiId);
      } else {
        expandedRfis.value.splice(index, 1);
      }
    };

    const viewRfiDetails = (rfi) => {
      selectedRfi.value = rfi;
    };

    const statuses = computed(() => {
      const allPriorities = activeRfis.value
        .map((rfi) => rfi.status)
        .filter(Boolean);
      return [...new Set(allPriorities)];
    });

    const disciplines = computed(() => {
      const allDisciplines = activeRfis.value
        .map((rfi) => rfi.COL_DISCIPLINE)
        .filter(Boolean);
      return [...new Set(allDisciplines)];
    });

    const activeRfis = computed(() => {
      if (allRfis.value.length > 0) {
        return allRfis.value;
      }
      return activeView.value === "requested"
        ? rfisRequested.value
        : rfisDirected.value;
    });

    const filteredRfis = computed(() => {
      let result = activeRfis.value;

      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter(
          (rfi) =>
            (rfi.status && rfi.status.toLowerCase().includes(query)) ||
            (rfi.requestor_name &&
              rfi.requestor_name.toLowerCase().includes(query)) ||
            (rfi.directed_to &&
              rfi.directed_to.toLowerCase().includes(query)) ||
            (rfi.ID && rfi.ID.toString().toLowerCase().includes(query))
        );
      }

      if (priorityFilter.value) {
        result = result.filter((rfi) => rfi.status === priorityFilter.value);
      }

      return result;
    });

    const paginatedRfis = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      return filteredRfis.value.slice(start, end);
    });

    const totalPages = computed(() => {
      return Math.ceil(filteredRfis.value.length / itemsPerPage);
    });

    onMounted(fetchRfis);

    const handleUpdateField = ({ field, value }) => {
      if (showCreateModal.value) {
        newRfi.value = {
          ...newRfi.value,
          [field]: value,
        };
      } else if (editMode.value && selectedRfi.value) {
        editedRfi.value = {
          ...editedRfi.value,
          [field]: value,
        };
      }
    };

    const handleExportToExcel = async () => {
      exporting.value = true;

      const items =
        allRfis.value.length > 0
          ? allRfis.value
          : activeView.value === "requested"
          ? rfisRequested.value
          : rfisDirected.value;

      try {
        const convertedObj = transformRFIArray(items);
        exportToExcel(convertedObj, "log--" + new Date());
      } catch (error) {
        console.log("🚀 ~ handleExportToExcel ~ error:", error);
      } finally {
        exporting.value = false;
      }
    };

    return {
      allRfis,
      rfisRequested,
      rfisDirected,
      loading,
      error,
      searchQuery,
      priorityFilter,
      disciplineFilter,
      expandedRfis,
      selectedRfi,
      currentPage,
      itemsPerPage,
      activeView,
      editMode,
      editedRfi,
      saving,
      editableFields,
      statuses,
      disciplines,
      activeRfis,
      filteredRfis,
      paginatedRfis,
      totalPages,
      toggleEditMode,
      closeModal,
      saveChanges,
      fetchRfis,
      formatDate,
      priorityClasses,
      toggleRfiDetails,
      viewRfiDetails,
      handleUpdateField,
      openCreateModal,
      createNewRfi,
      showCreateModal,
      newRfi,
      userColumns,
      loadingModal,
      priorities,
      handleExportToExcel,
    };
  },
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>